import type { ReactNode } from 'react'
import keys from 'lodash/keys'
import { AlertTriangle } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@klear/ui/components/alert'
import { Button } from '@klear/ui/components/button'
import type { EventFormBootstrap } from '@klear/order-event-ui/event-shared/use-event-form-bootstrap'
import {
  EventFormControlProvider,
  createEventFormSubmitHandler
} from '@klear/order-event-ui/event-shared/event-form-control'
import type { EventFormState } from '@klear/order-event-ui/event-shared/event-form-state'

export type EventFormShellProps<T extends Record<string, unknown>> = {
  formState: EventFormState<T>
  title: string
  isLoading: boolean
  onSubmit: (data: T) => void | Promise<void>
  onCancel?: () => void
  children: ReactNode
}

export function EventFormShell<T extends Record<string, unknown>>({
  formState,
  title,
  isLoading,
  onSubmit,
  onCancel,
  children
}: EventFormShellProps<T>) {
  const formErrors = formState.errors
  const hasFieldErrors = keys(formErrors).some(
    (key) => key !== 'root' && Boolean((formErrors as Record<string, unknown>)[key])
  )
  const rootError = formErrors.root
  const hasRootError = Boolean(rootError)

  const handleSubmit = createEventFormSubmitHandler(formState, onSubmit)

  return (
    <form onSubmit={handleSubmit}>
      <EventFormControlProvider formState={formState}>
        <div className="space-y-4">
          <h2 className="pt-4 text-lg font-semibold">{title}</h2>
          {hasRootError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Validation Error</AlertTitle>
              <AlertDescription>{rootError || 'Please fix the errors below before saving.'}</AlertDescription>
            </Alert>
          )}
          {hasFieldErrors && !rootError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Please fix the following errors:</AlertTitle>
              <AlertDescription>
                <ul className="list-disc list-inside space-y-1">{formErrors.files && <li>{formErrors.files}</li>}</ul>
              </AlertDescription>
            </Alert>
          )}
          {children}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </EventFormControlProvider>
    </form>
  )
}

export type EventFormHandlerProps = {
  bootstrap: EventFormBootstrap
  orderId: number
  traderId: number
  isAdminContext?: boolean
  onSuccess?: (savedEventId?: number) => void
  onCancel?: () => void
}
